import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  deleteSuperTrack,
  updateSelectedTracks,
  updateHighlightedSuperTracks,
  setSelectedTracks,
  setSelectedSuperTrack,
  setSelectedSuperTracks,
} from "@/store/supertrackSlice";
import { matchApi } from "../../api";
import { cloneDeep } from "lodash";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export function SuperTrackMarkupControls({
  superTrack,
  onHide,
  showExtra = true,
  task,
  isManager = false, // Add this prop
}) {
  const { videoId: matchId } = useParams();
  const dispatch = useDispatch();
  const { superTracks, selectedSuperTrack, selectedSuperTracks } = useSelector(
    ({ superTracks }) => superTracks
  );

  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    if (!selectedSuperTracks || !superTrack) return;
    if (selectedSuperTracks[superTrack.id]) setIsSelected(true);
    else setIsSelected(false);
  }, [superTrack, selectedSuperTracks]);

  const onSuperTrackClick = () => {
    if (superTrack.isHidden) return;
    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    clonedSelectedSuperTracks[superTrack.id] = !isSelected;
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));

    dispatch(
      updateSelectedTracks({
        isAdding: !isSelected,
        ids: superTrack.tracks.map(({ track_id }) => track_id),
      })
    );

    setIsSelected(!isSelected);
  };

  const removeSuperTrack = async () => {
    const confirm = window.confirm("Do you really want to delete this?");

    if (!confirm) return;

    await matchApi.deleteSuperTrack({ matchId, superTrackId: superTrack.id });
    dispatch(deleteSuperTrack(superTrack.id));

    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    delete clonedSelectedSuperTracks[superTrack.id];
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));
  };

  const editMarkUpUrl = `/panel/markup/super-tracks/${matchId}/markup/${superTrack.id}`;

  const findLost = () => {
    if (!superTrack) {
      console.log("no super track");
      return;
    }
    if (!superTracks) {
      console.log("no super tracks");
      return;
    }
    // find possible next super tracks
    let lastFrame = 0;
    superTrack.tracks.forEach(({ frame_end }) => {
      if (frame_end > lastFrame) lastFrame = frame_end;
    });
    let highlightedSuperTracks = [];
    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    for (let id in superTracks) {
      const isPossible = superTracks[id].tracks.every(
        // eslint-disable-next-line no-loop-func
        ({ frame_start }) => frame_start > lastFrame
      );
      if (isPossible) {
        highlightedSuperTracks.push(id);
        clonedSelectedSuperTracks[id] = true;
      } else {
        clonedSelectedSuperTracks[id] = false;
      }
    }
    dispatch(updateHighlightedSuperTracks(highlightedSuperTracks));
    // select the track itself
    clonedSelectedSuperTracks[superTrack.id] = true;
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));

    let selectedTrackIds = [];
    [...highlightedSuperTracks, superTrack.id].forEach((id) => {
      const trackIds = superTracks[id].tracks.map(({ track_id }) => track_id);
      selectedTrackIds = [...selectedTrackIds, ...trackIds];
    });
    dispatch(setSelectedTracks(selectedTrackIds));

    dispatch(setSelectedSuperTrack({ ...superTrack }));
  };

  // Helper to format ISO date string to "DD/MM/YYYY HH:mm:ss UTC"
  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    if (isNaN(date)) return isoString; // fallback if invalid
    const pad = (n) => n.toString().padStart(2, '0');
    return (
      `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()} ` +
      `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`
    );
  };

  return (
    <div
      className={`d-flex align-items-center flex-nowrap ${
        showExtra
          ? "super-track-control__container_extra"
          : "super-track-control__container"
      }`}
    >
      {showExtra && (
        <>
          {isManager ? (
            <FontAwesomeIcon
              className="text-danger"
              icon={["fas", "trash"]}
              onClick={removeSuperTrack}
            />
          ) : <span></span>}
          <FontAwesomeIcon
            className={superTrack.isHidden ? "text-grey" : "text-success"}
            icon={["fas", superTrack.isHidden ? "eye-slash" : "eye"]}
            onClick={() => onHide(superTrack.id)}
          />
          <FontAwesomeIcon
            className={isSelected ? "text-success" : "text-grey"}
            icon={["fas", "check"]}
            onClick={onSuperTrackClick}
          />
          <a href={editMarkUpUrl} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={["fas", "edit"]} />
          </a>
          <FontAwesomeIcon
            className={
              selectedSuperTrack?.id === superTrack.id
                ? "text-success"
                : "text-grey"
            }
            icon={["fas", "search"]}
            onClick={findLost}
          />
          {task && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-${superTrack.id}`}>
                  <div className="text-start">
                    <div>{task.resolver_name || 'Not assigned'}</div>
                    <div>
                      Taken: {task.last_taken_at ? formatDate(task.last_taken_at) : 'Not taken'}
                    </div>
                    <div>
                      Resolved: {task.resolved_at ? formatDate(task.resolved_at) : 'Not resolved'}
                    </div>
                  </div>
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                className={
                  task?.status === "finished"
                    ? "text-success"
                    : task?.status === "in progress"
                    ? "text-primary"
                    : "text-dark"
                }
                icon="fa-solid fa-circle"
              />
            </OverlayTrigger>
          )}
          <span className="text-muted small">#{superTrack.id}</span>
        </>
      )}
    </div>
  );
}
